import { AutoComplete, Col, Input, Row, Select, Spin } from "antd";
import { I18N_NS } from "Config/i18n";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "Components/General/Button";
import debounce from "lodash/debounce";
import { LabeledValue } from "antd/lib/select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface AttributeSelectProps {
  onChange?: (v: API_IfcPropertyDetail[]) => void;
  value?: API_IfcPropertyDetail[];
  fetchOptions?: (value: string) => Promise<API_IfcPropertyDetail[]>;
}

interface AttributeControlProps {
  value: API_IfcPropertyDetail;
  fetchOptions: AttributeSelectProps["fetchOptions"];
  onChange: (v: API_IfcPropertyDetail) => void;
  onRemove: () => void;
}

const AttributeControl = ({ fetchOptions, value, onChange, onRemove }: AttributeControlProps) => {
  const { t } = useTranslation(I18N_NS.PROJECT);
  const [fetching, setFetching] = React.useState(false);
  const [options, setOptions] = React.useState<LabeledValue[]>([]);
  const [selectedProperty, setSelectedProperty] = React.useState<API_IfcPropertyDetail>(null);
  const fetchRef = React.useRef(0);

  const handleAttributePropChange = (_, option) => {
    setSelectedProperty(option.property);
    onChange({ value: "", name: option.label });
    setSelectedProperty(option.property);
  };

  const debounceFetcher = React.useMemo(() => {
    const loadOptions = (value: string) => {
      console.log("loadOptions", value);
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      setOptions([]);
      setFetching(true);
      // if (!value.length) {
      //   setOptions([]);
      // } else {
      fetchOptions(value).then((newOptions) => {
        if (fetchId !== fetchRef.current) {
          // for fetch callback order
          return;
        }
        setOptions(newOptions.map((i) => ({ label: i.name, value: i.name, property: i })));
        setFetching(false);
      });
      // }
    };

    return debounce(loadOptions, 800);
  }, [fetchOptions]);

  return (
    <Row gutter={10} className="mb-2">
      <Col span={11}>
        <Select<LabeledValue>
          placeholder={t("Attribute name")}
          labelInValue
          showSearch={{ filterOption: false, onSearch: debounceFetcher }}
          notFoundContent={fetching ? <Spin size="small" /> : null}
          options={options}
          allowClear={false}
          onSelect={handleAttributePropChange}
          className="attribute-search"
          getPopupContainer={() => document.getElementById("xkt-search-form")}
        />
      </Col>
      <Col span={11}>
        <AutoComplete
          value={value.value}
          onChange={(val) => onChange({ value: val, name: value.name })}
          options={selectedProperty?.options?.map((i) => ({
            value: i.trim(),
            label: i.trim(),
          }))}
          filterOption={(inputValue, option) => option?.value.toLowerCase().includes(inputValue.toLowerCase())}
        >
          <Input placeholder={t("Attribute value")} suffix={selectedProperty?.unit} />
        </AutoComplete>
      </Col>
      <Col span={2} className="flex-center">
        <span style={{ cursor: "pointer" }} onClick={onRemove}>
          <FontAwesomeIcon icon={["far", "minus-circle"]} fixedWidth={true} size="lg" />
        </span>
      </Col>
    </Row>
  );
};

export const AttributeSelect = ({ onChange, value, fetchOptions }: AttributeSelectProps) => {
  const { t } = useTranslation(I18N_NS.PROJECT);

  const handleAdd = () => {
    onChange(value.concat({ name: "", value: "" }));
  };

  const handleRemove = (idx: number) => {
    const copy = value.slice();
    copy.splice(idx, 1);
    onChange(copy);
  };

  const handleAttributeChange = (idx: number) => (changedValue: API_IfcPropertyDetail) => {
    const updatedValues = value.slice();
    updatedValues[idx] = { ...changedValue };
    onChange(updatedValues);
  };

  return (
    <div className="mb-1">
      {value.map((item, idx) => (
        <AttributeControl
          key={idx}
          fetchOptions={fetchOptions}
          value={item}
          onChange={handleAttributeChange(idx)}
          onRemove={() => handleRemove(idx)}
        />
      ))}
      <Button faIcon="plus" type="dashed" onClick={handleAdd}>
        {t("Add")}
      </Button>
    </div>
  );
};
